import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, Wrench, Info, Search,
} from "lucide-react";

const SEV_STYLE = {
  critical: { bg: "bg-red-100", fg: "text-red-700", border: "border-red-200", icon: XCircle, label: "CRÍTICO" },
  high:     { bg: "bg-orange-100", fg: "text-orange-700", border: "border-orange-200", icon: AlertTriangle, label: "ALTO" },
  medium:   { bg: "bg-amber-100", fg: "text-amber-700", border: "border-amber-200", icon: AlertTriangle, label: "MEDIO" },
  low:      { bg: "bg-slate-100", fg: "text-slate-700", border: "border-slate-200", icon: Info, label: "BAJO" },
};

const STATUS_STYLE = {
  ok:       { bg: "bg-emerald-100", fg: "text-emerald-700", icon: CheckCircle2, label: "TODO OK" },
  warnings: { bg: "bg-amber-100", fg: "text-amber-700", icon: AlertTriangle, label: "AVISOS" },
  issues:   { bg: "bg-orange-100", fg: "text-orange-700", icon: AlertTriangle, label: "INCIDENCIAS" },
  critical: { bg: "bg-red-100", fg: "text-red-700", icon: XCircle, label: "CRÍTICO" },
};

export default function PermissionsAuditPage() {
  const { axiosAuth, isSuperAdmin } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [endpointFilter, setEndpointFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosAuth().get("/admin/health/permissions-audit");
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando auditoría");
    } finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const handleRepair = async () => {
    if (confirmText !== "REPARAR") return;
    setRepairing(true);
    try {
      const r = await axiosAuth().post("/admin/health/permissions-audit/repair-base-roles?confirm=REPARAR");
      toast.success(r.data.message || "Roles reparados");
      setConfirmOpen(false); setConfirmText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error reparando roles");
    } finally { setRepairing(false); }
  };

  if (!isSuperAdmin) return (
    <div className="p-8 text-center" data-testid="audit-forbidden">
      <ShieldAlert className="w-10 h-10 text-red-300 mx-auto mb-3" />
      <p className="text-sm font-semibold text-red-700">Solo el super_admin puede ver la auditoría.</p>
    </div>
  );

  if (loading) return <p className="p-6 text-sm text-slate-400">Cargando auditoría…</p>;
  if (!audit) return <p className="p-6 text-sm text-slate-400">Sin datos.</p>;

  const { summary, modules, endpoints, roles, users_with_invalid_role, findings, generated_at } = audit;
  const StatusBadge = STATUS_STYLE[summary.overall_status] || STATUS_STYLE.warnings;
  const StatusIcon = StatusBadge.icon;

  const filteredEndpoints = endpoints.filter(ep =>
    !endpointFilter || ep.path.toLowerCase().includes(endpointFilter.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="audit-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sistema · Auditoría</p>
          <h1 className="text-3xl font-light tracking-tight text-slate-900">Permisos y Datos</h1>
          <p className="text-xs text-slate-500 mt-2">
            Generado: {new Date(generated_at).toLocaleString("es-ES")} · {summary.endpoints_total} endpoints · {summary.roles_in_db} roles · {summary.modules_total} módulos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${StatusBadge.bg} ${StatusBadge.fg} rounded-sm border-0`} data-testid="audit-overall-status">
            <StatusIcon className="w-3 h-3 mr-1" /> {StatusBadge.label}
          </Badge>
          <Button onClick={load} variant="outline" size="sm" className="rounded-sm text-xs uppercase" data-testid="audit-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Recargar
          </Button>
          <Button onClick={() => setConfirmOpen(true)} className="rounded-sm text-xs uppercase bg-orange-600 hover:bg-orange-700 text-white" data-testid="audit-repair-btn">
            <Wrench className="w-3.5 h-3.5 mr-1.5" /> Reparar roles base
          </Button>
        </div>
      </div>

      {/* Resumen */}
      <Card className="rounded-sm border-slate-200" data-testid="audit-summary">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries({
            critical: "Crítico", high: "Alto", medium: "Medio", low: "Bajo",
          }).map(([sev, label]) => {
            const count = summary.findings_by_severity?.[sev] || 0;
            const style = SEV_STYLE[sev];
            return (
              <div key={sev} className={`p-3 rounded-sm border ${style.border} ${style.bg}/40`} data-testid={`audit-count-${sev}`}>
                <p className={`text-[10px] uppercase tracking-wider ${style.fg}`}>{label}</p>
                <p className={`text-2xl font-light ${style.fg}`}>{count}</p>
              </div>
            );
          })}
          <div className="p-3 rounded-sm border border-slate-200 bg-slate-50">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">Endpoints</p>
            <div className="text-xs space-y-0.5 mt-1 text-slate-700">
              {Object.entries(summary.endpoints_by_guard || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="findings" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="findings" data-testid="tab-findings">Incidencias ({findings.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles ({roles.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Usuarios rotos ({users_with_invalid_role.length})</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">Módulos ({modules.all.length})</TabsTrigger>
          <TabsTrigger value="endpoints" data-testid="tab-endpoints">Endpoints ({endpoints.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="findings" className="mt-4">
          {findings.length === 0 ? (
            <Card className="rounded-sm border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-700">No se han detectado incidencias.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2" data-testid="audit-findings-list">
              {findings.map((f, i) => {
                const s = SEV_STYLE[f.severity] || SEV_STYLE.low;
                const Icon = s.icon;
                return (
                  <Card key={i} className={`rounded-sm ${s.border}`}>
                    <CardContent className="p-3 flex items-start gap-3">
                      <Icon className={`w-4 h-4 mt-0.5 ${s.fg} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className={`${s.bg} ${s.fg} text-[10px] rounded-sm border-0`}>{s.label}</Badge>
                          <code className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{f.code}</code>
                          {f.role && <Badge variant="outline" className="text-[10px] rounded-sm">rol: {f.role}</Badge>}
                          {f.module && <Badge variant="outline" className="text-[10px] rounded-sm">módulo: {f.module}</Badge>}
                        </div>
                        <p className="text-xs text-slate-800">{f.message}</p>
                        {f.fix && <p className="text-[11px] text-slate-500 mt-1 italic">💡 {f.fix}</p>}
                        {f.missing_modules && (
                          <p className="text-[11px] text-slate-600 mt-1">
                            Faltantes: {f.missing_modules.map(m => <code key={m} className="bg-slate-100 px-1 rounded mx-0.5">{m}</code>)}
                          </p>
                        )}
                        {f.email && <p className="text-[11px] text-slate-600 mt-1">Usuario: <code>{f.email}</code></p>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          <Card className="rounded-sm">
            <CardContent className="p-0">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr><th className="text-left p-2">Slug</th><th className="text-left p-2">Nombre</th><th className="text-left p-2">Level</th><th className="text-left p-2">Módulos</th><th className="text-left p-2">Activo</th></tr>
                </thead>
                <tbody>
                  {roles.map(r => (
                    <tr key={r.slug} className="border-t border-slate-100" data-testid={`audit-role-${r.slug}`}>
                      <td className="p-2 font-mono">{r.slug}</td>
                      <td className="p-2">{r.name}</td>
                      <td className="p-2">{r.level}</td>
                      <td className="p-2 text-[10px] text-slate-500">{Object.keys(r.permissions || {}).length} módulos</td>
                      <td className="p-2">{r.active ? <Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-sm text-[10px]">SÍ</Badge> : <Badge className="bg-slate-100 text-slate-500 border-0 rounded-sm text-[10px]">NO</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          {users_with_invalid_role.length === 0 ? (
            <p className="text-sm text-slate-500 p-4">No hay usuarios con rol inválido. ✓</p>
          ) : (
            <Card className="rounded-sm border-orange-200">
              <CardContent className="p-0">
                <table className="w-full text-xs">
                  <thead className="bg-orange-50"><tr><th className="text-left p-2">Email</th><th className="text-left p-2">Rol inválido</th><th className="text-left p-2">Activo</th></tr></thead>
                  <tbody>
                    {users_with_invalid_role.map(u => (
                      <tr key={u.id} className="border-t border-slate-100">
                        <td className="p-2">{u.email}</td>
                        <td className="p-2"><code className="bg-red-50 text-red-700 px-1 rounded">{u.role}</code></td>
                        <td className="p-2">{u.active ? "SÍ" : "NO"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="modules" className="mt-4">
          <div className="grid md:grid-cols-3 gap-3">
            {[["Todos", modules.all], ["En endpoints", modules.in_endpoint_guards], ["En BD", modules.in_db_roles]].map(([title, list]) => (
              <Card key={title} className="rounded-sm">
                <CardContent className="p-3">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{title} ({list.length})</p>
                  <div className="flex flex-wrap gap-1">
                    {list.map(m => <code key={m} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{m}</code>)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="endpoints" className="mt-4">
          <div className="mb-3 relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
            <Input value={endpointFilter} onChange={e => setEndpointFilter(e.target.value)} placeholder="Filtrar por ruta…" className="pl-8 rounded-sm text-xs h-8" data-testid="audit-ep-filter" />
          </div>
          <Card className="rounded-sm">
            <CardContent className="p-0 max-h-[600px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left p-2">Métodos</th><th className="text-left p-2">Ruta</th><th className="text-left p-2">Guard</th><th className="text-left p-2">Detalle</th></tr></thead>
                <tbody>
                  {filteredEndpoints.map((ep, i) => {
                    const g = ep.guard;
                    const gtype = g?.type || "none";
                    const noGuardWarn = !g && ep.is_admin_path;
                    return (
                      <tr key={i} className={`border-t border-slate-100 ${noGuardWarn ? "bg-red-50" : ""}`}>
                        <td className="p-2 font-mono text-[10px]">{ep.methods.join(",")}</td>
                        <td className="p-2 font-mono">{ep.path}</td>
                        <td className="p-2"><code className={`text-[10px] px-1 rounded ${noGuardWarn ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{gtype}</code></td>
                        <td className="p-2 text-[10px] text-slate-500">
                          {g?.module && <>módulo: <code>{g.module}</code> · acción: {g.action}</>}
                          {g?.allowed_roles && <>roles: {g.allowed_roles.join(", ")}</>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setConfirmText(""); }}>
        <DialogContent className="rounded-sm" data-testid="audit-repair-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Wrench className="w-4 h-4" /> Reparar roles base
            </DialogTitle>
            <DialogDescription className="text-xs leading-5">
              Esta acción es <strong>idempotente y additiva</strong>: añade módulos faltantes a los roles base (super_admin, admin, jefe_taller, jefe_recambios, rrhh, recepcion, empleado).
              <br/><br/>
              <strong>Nunca quita</strong> permisos existentes. Los roles custom NO se modifican.
              <br/><br/>
              Para confirmar, escribe <code className="bg-slate-100 px-1.5 py-0.5 rounded font-semibold">REPARAR</code> abajo:
            </DialogDescription>
          </DialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Escribe REPARAR" className="rounded-sm" data-testid="audit-confirm-text" autoFocus />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleRepair} disabled={confirmText !== "REPARAR" || repairing} className="rounded-sm text-xs uppercase bg-orange-600 hover:bg-orange-700 text-white" data-testid="audit-confirm-repair-btn">
              {repairing ? "Reparando…" : "Reparar ahora"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
